import { onClick } from '@nx-paeonia/utils-events';
import { isErr, ok, Result } from '@nx-paeonia/utils-result';
import { animate, AnimationSequence, resize } from 'motion';
import { toUniqueSelector } from '../helpers/selector.helper.js';

type CollapsibleOn = 'never' | 'always' | 'mobile' | 'desktop';

export function collapsibleResizeFactory(elements: NodeListOf<HTMLElement>): void {
  if (!elements.length) {
    return;
  }

  resize((info) => {
    elements.forEach((element) => {
      const collapsibleOn = getElementCollapsibleOn(element);

      if (isErr(collapsibleOn)) {
        console.error('Invalid collapsibleOn value:', collapsibleOn.error);
        return;
      }

      switch (collapsibleOn.data) {
        case 'never':
          element.setAttribute('open', '');
          break;
        case 'always':
          element.removeAttribute('open');
          break;
        case 'mobile':
          if (info.width > 768) {
            element.setAttribute('open', '');
          } else {
            element.removeAttribute('open');
          }
          break;
        case 'desktop':
          if (info.width <= 768) {
            element.setAttribute('open', '');
          } else {
            element.removeAttribute('open');
          }
          break;
      }

      if (collapsibleOn.data === 'never') {
        element.setAttribute('open', '');
      }

      if (collapsibleOn.data === 'always') {
        element.removeAttribute('open');
      }

      if (collapsibleOn.data === 'mobile' && info.width > 768) {
        element.setAttribute('open', '');
      }

      if (collapsibleOn.data === 'desktop' && info.width <= 768) {
        element.setAttribute('open', '');
      }
    });
  });
}

export function collapsibleOnClickFactory(elements: NodeListOf<HTMLElement>): void {
  if (!elements.length) {
    return;
  }

  elements.forEach((element) => {
    onClick(toUniqueSelector(element), (event, element) => {
      event.preventDefault();
      const collapsibleOn = getElementCollapsibleOn(element);

      if (isErr(collapsibleOn)) {
        console.error('Invalid collapsibleOn value:', collapsibleOn.error);
        return;
      }

      if (collapsibleOn.data === 'never') {
        return;
      }

      const parent = element.closest('details');

      if (collapsibleOn.data === 'always') {
        if (parent?.hasAttribute('open')) {
          closeCollapsible(parent);
        } else if (parent) {
          openCollapsible(parent);
        }
        return;
      }

      if (collapsibleOn.data === 'mobile' && window.innerWidth <= 768) {
        if (parent?.hasAttribute('open')) {
          closeCollapsible(parent);
        } else if (parent) {
          openCollapsible(parent);
        }
      }

      if (collapsibleOn.data === 'desktop' && window.innerWidth > 768) {
        if (parent?.hasAttribute('open')) {
          closeCollapsible(parent);
        } else if (parent) {
          openCollapsible(parent);
        }
      }
    });
  });
}

function getElementCollapsibleOn(element: HTMLElement): Result<CollapsibleOn, 'always'> {
  const collapsibleOn = element.dataset.collapsibleOn;
  if (collapsibleOn === 'never' || collapsibleOn === 'always' || collapsibleOn === 'mobile' || collapsibleOn === 'desktop') {
    return ok(collapsibleOn as CollapsibleOn);
  }
  return ok('always');
}

function openCollapsible(element: HTMLDetailsElement): void {
  const animationSequence: AnimationSequence = [];

  const summary = element.querySelector('summary');
  if (summary) {
    animationSequence.push([summary, { '--collapsible-summary-after-scale': [1, 0] }]);
  }

  const body = element.querySelector(':scope > div');
  if (body) {
    animationSequence.push([
      body,
      { height: [0, body.scrollHeight], opacity: [0, 0, 1], translateY: [10, 10, 0], translateX: [10, 10, 0], overflow: 'hidden' },
    ]);
  }

  const animation = animate(animationSequence);
  animation.cancel();
  element.setAttribute('open', '');
  animation.play();
}

function closeCollapsible(element: HTMLDetailsElement): void {
  const animationSequence: AnimationSequence = [];

  const body = element.querySelector(':scope > div');
  if (body) {
    animationSequence.push([
      body,
      { height: [body.scrollHeight, 0], opacity: [1, 0], translateY: [0, 10], translateX: [0, 10], overflow: 'hidden' },
    ]);
  }

  const summary = element.querySelector('summary');
  if (summary) {
    animationSequence.push([summary, { '--collapsible-summary-after-scale': [0, 1] }]);
  }

  const animation = animate(animationSequence);

  animation.finished.then(() => {
    element.removeAttribute('open');
  });
}
